import { useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";
import SocialIcons from "@/components/SocialIcons";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MoveRight, MapPin, Clock, Phone, Mail } from "lucide-react";

export default function Contact() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("contact.sent"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-24 pb-32">
      <section className="pt-20 pb-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <p className="text-eyebrow text-brand mb-6">{t("contact.eyebrow")}</p>
          <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl text-ink leading-[0.95] tracking-tighter">
            {t("contact.title")}
          </h1>
          <p className="mt-8 text-lg text-ink-muted font-light leading-relaxed max-w-xl">
            {t("contact.subtitle")}
          </p>

          <div className="mt-12 space-y-6">
            {settings.phone && (
              <a
                data-testid="contact-phone-link"
                href={`tel:${settings.phone.replace(/\s/g, "")}`}
                className="flex items-start gap-4 group"
              >
                <Phone className="text-brand mt-1" size={20} />
                <div>
                  <p className="text-eyebrow text-ink-dim mb-1">{t("contact.phone")}</p>
                  <p className="text-ink group-hover:text-brand transition-colors">
                    {settings.phone}
                  </p>
                </div>
              </a>
            )}
            {settings.email && (
              <a
                data-testid="contact-email-link"
                href={`mailto:${settings.email}`}
                className="flex items-start gap-4 group"
              >
                <Mail className="text-brand mt-1" size={20} />
                <div>
                  <p className="text-eyebrow text-ink-dim mb-1">{lang === "en" ? "Email" : "Email"}</p>
                  <p className="text-ink group-hover:text-brand transition-colors">
                    {settings.email}
                  </p>
                </div>
              </a>
            )}
            <div className="flex items-start gap-4">
              <MapPin className="text-brand mt-1" size={20} />
              <div>
                <p className="text-eyebrow text-ink-dim mb-1">{lang === "en" ? "Workshop" : "Taller"}</p>
                <p className="text-ink">
                  {lang === "en"
                    ? settings.address_en || t("contact.address")
                    : settings.address_es || t("contact.address")}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock className="text-brand mt-1" size={20} />
              <div>
                <p className="text-eyebrow text-ink-dim mb-1">{lang === "en" ? "Hours" : "Horario"}</p>
                <p className="text-ink">
                  {lang === "en"
                    ? settings.hours_en || t("contact.hours")
                    : settings.hours_es || t("contact.hours")}
                </p>
              </div>
            </div>

            <div className="pt-6 border-t border-edge">
              <p className="text-eyebrow text-ink-muted mb-4">{t("contact.followUs")}</p>
              <SocialIcons size={20} testIdPrefix="contact-social" />
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          className="md:col-span-5 bg-bg-surface border border-edge p-8 md:p-10 space-y-5"
        >
          <Field
            testId="contact-name"
            label={t("contact.name")}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <Field
            testId="contact-email"
            type="email"
            label={t("contact.email")}
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            required
          />
          <Field
            testId="contact-phone"
            label={t("contact.phone")}
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
          />
          <Field
            testId="contact-subject"
            label={t("contact.subject")}
            value={form.subject}
            onChange={(v) => setForm({ ...form, subject: v })}
          />
          <div>
            <label className="text-eyebrow text-ink-muted block mb-2">{t("contact.message")}</label>
            <textarea
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              rows={5}
              className="w-full bg-transparent border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand transition-colors"
            />
          </div>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={submitting}
            className="group w-full py-4 bg-brand text-ink-inverse text-sm font-medium tracking-wide hover:bg-brand-hover transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {submitting ? "…" : t("contact.send")}
            {!submitting && <MoveRight size={16} className="group-hover:translate-x-1 transition-transform" />}
          </button>
          {sent && (
            <p data-testid="contact-sent" className="text-sm text-brand text-center">
              {t("contact.sent")}
            </p>
          )}
        </form>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-eyebrow text-ink-muted block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand transition-colors"
      />
    </div>
  );
}
